import { useMemo } from 'react';

import { DateTime, Duration } from 'luxon';

import {
  AvailabilityFragment,
  Currency,
  MultidayAvailabilityFragment,
} from '@graphql/platform';

import type { SharedCheckoutDateScheduled } from '@root/components/checkout/data';

import {
  FROM_ISO_OPTIONS,
  hashDateTime,
} from '@shared/calendar/calendar_utils';

export type ChallengerAvailability =
  | AvailabilityFragment
  | MultidayAvailabilityFragment;

type WindowClassification = Pick<
  ChallengerAvailability,
  'duration' | 'fromTime' | 'tillTime'
> & {
  feeAmount?: { value?: number | null } | null;
};

const hasPaidArrivalFee = (
  availability: Pick<WindowClassification, 'feeAmount'>,
): boolean => (availability.feeAmount?.value ?? 0) > 0;

/** Flexible / all-day windows are ≥ 8 hours (same rule as classic ArrivalWindowSelector). */
const ALL_DAY_DURATION = Duration.fromObject({ hours: 8 });

/**
 * Prefer API `duration`; fall back to till − from so short windows still classify
 * as Scheduled when duration is omitted.
 */
const availabilityDuration = (
  availability: Pick<
    ChallengerAvailability,
    'duration' | 'fromTime' | 'tillTime'
  >,
): Duration | undefined => {
  if (availability.duration) {
    return Duration.fromISO(availability.duration);
  }
  if (!availability.fromTime || !availability.tillTime) return undefined;
  const from = DateTime.fromISO(availability.fromTime, FROM_ISO_OPTIONS);
  const till = DateTime.fromISO(availability.tillTime, FROM_ISO_OPTIONS);
  if (!from.isValid || !till.isValid) return undefined;
  return till.diff(from);
};

export const isFlexibleAvailability = (
  availability: WindowClassification,
): boolean => {
  if (hasPaidArrivalFee(availability)) return false;
  const duration = availabilityDuration(availability);
  return !!duration && duration >= ALL_DAY_DURATION;
};

export const isScheduledAvailability = (
  availability: WindowClassification,
): boolean => {
  if (hasPaidArrivalFee(availability)) return true;
  const duration = availabilityDuration(availability);
  return !!duration && duration < ALL_DAY_DURATION;
};

/** Compare windows by instant, not raw ISO strings (draft/session vs API). */
export const availabilityWindowMatches = (
  a: Pick<ChallengerAvailability, 'fromTime' | 'tillTime'>,
  b: Pick<ChallengerAvailability, 'fromTime' | 'tillTime'>,
): boolean => {
  const aFrom = DateTime.fromISO(a.fromTime, FROM_ISO_OPTIONS);
  const aTill = DateTime.fromISO(a.tillTime, FROM_ISO_OPTIONS);
  const bFrom = DateTime.fromISO(b.fromTime, FROM_ISO_OPTIONS);
  const bTill = DateTime.fromISO(b.tillTime, FROM_ISO_OPTIONS);
  if (!aFrom.isValid || !aTill.isValid || !bFrom.isValid || !bTill.isValid) {
    return a.fromTime === b.fromTime && a.tillTime === b.tillTime;
  }
  return (
    aFrom.toMillis() === bFrom.toMillis() &&
    aTill.toMillis() === bTill.toMillis()
  );
};

export const findMatchingAvailability = <T extends ChallengerAvailability>(
  saved: Pick<T, 'fromTime' | 'tillTime'> & {
    feeAmount?: { value?: number | null } | null;
  },
  availabilities: ReadonlyArray<T>,
): T | undefined => {
  const exact = availabilities.find((availability) =>
    availabilityWindowMatches(availability, saved),
  );
  if (exact) return exact;

  if (!hasPaidArrivalFee(saved)) return undefined;

  const savedFrom = DateTime.fromISO(saved.fromTime, FROM_ISO_OPTIONS);
  if (!savedFrom.isValid) return undefined;

  return availabilities.find((availability) => {
    if (!isScheduledAvailability(availability)) return false;
    const from = DateTime.fromISO(availability.fromTime, FROM_ISO_OPTIONS);
    return from.isValid && from.toMillis() === savedFrom.toMillis();
  });
};

/** Map API availability fragments to checkout `dateScheduled` shape. */
export const toCheckoutDateScheduled = (
  availability: ChallengerAvailability,
): SharedCheckoutDateScheduled => ({
  fromTime: availability.fromTime,
  tillTime: availability.tillTime,
  formatted: availability.formatted,
  duration: availability.duration ?? undefined,
  feeAmount: availability.feeAmount
    ? {
        currency:
          'currency' in availability.feeAmount &&
          availability.feeAmount.currency != null
            ? availability.feeAmount.currency
            : Currency.Usd,
        value: availability.feeAmount.value,
        formatted: availability.feeAmount.formatted,
      }
    : null,
});

/** First scheduled-window fee in a loaded availability payload (ZIP-level). */
export const getScheduledArrivalFee = (
  availabilities: ReadonlyArray<ChallengerAvailability>,
): number | undefined => {
  for (const availability of availabilities) {
    if (!isScheduledAvailability(availability)) continue;
    const value = availability.feeAmount?.value;
    if (value != null) return value;
  }
  return undefined;
};

/**
 * Challenger-only day filter for appointment windows.
 * Compares calendar days (not absolute millis) so offset differences between
 * API windows and local MockDate still match.
 */
export const useChallengerFilteredAvailabilities = (
  availabilities: ReadonlyArray<ChallengerAvailability>,
  datePreferred?: DateTime,
) => {
  return useMemo(() => {
    if (!datePreferred) return [];

    return availabilities
      .filter((availability) => {
        const availabilityDate = DateTime.fromISO(
          availability.fromTime,
          FROM_ISO_OPTIONS,
        );
        if (!availabilityDate.isValid) return false;
        const sameDay =
          hashDateTime(availabilityDate) === hashDateTime(datePreferred);
        const availabilityLocal = availabilityDate.toLocal();
        const notPast =
          availabilityLocal.startOf('day') >= DateTime.now().startOf('day');
        return sameDay && notPast;
      })
      .sort(
        (a, b) =>
          DateTime.fromISO(a.fromTime, FROM_ISO_OPTIONS).toMillis() -
          DateTime.fromISO(b.fromTime, FROM_ISO_OPTIONS).toMillis(),
      );
  }, [availabilities, datePreferred]);
};
